import { useCallback, useEffect, useRef, useState } from 'react'
import {
  ConnectionQuality,
  ConnectionState,
  DisconnectReason,
  Room,
  RoomEvent,
} from 'livekit-client'

import { requestVpnAssessment, type VpnAssessment } from '@/src/lib/vpnAssessment'

/** Один заход переживает замену Room при retry, но не выход или смену комнаты. */
interface CallEntry {
  roomName: string
  active: boolean
  requested: boolean
  warned: boolean
  manualRetry: boolean
  controller?: AbortController
  timeout?: ReturnType<typeof setTimeout>
}

/** Снимок React-state принадлежит конкретному заходу, а не имени комнаты в глобальном кеше. */
interface HintState {
  entry: CallEntry
  assessment: VpnAssessment | null
  problem: boolean
}

/** Параметры активного захода; замена SDK-комнаты не начинает новый заход. */
interface VpnHintOptions {
  room: Room
  roomName: string
  accessToken?: string
  enabled?: boolean
}

/**
 * Проверяет VPN один раз при начале connect и запоминает вердикт до выхода из звонка.
 *
 * Плашка разрешена при score >= 1 и локальном Poor/Lost или сетевом reconnect.
 * Замена Room и обновление JWT не сбрасывают оценку; поздний ответ после выхода игнорируется.
 *
 * @param options — текущая комната, её JWT и признак обычного участника.
 */
export function useVpnConnectionHint({
  room,
  roomName,
  accessToken,
  enabled = true,
}: VpnHintOptions) {
  const entryRef = useRef<CallEntry | null>(null)
  const [state, setState] = useState<HintState | null>(null)

  useEffect(() => {
    const entry: CallEntry = {
      roomName,
      active: true,
      requested: false,
      warned: false,
      manualRetry: false,
    }

    entryRef.current = entry
    setState({ entry, assessment: null, problem: false })

    return () => {
      entry.active = false
      entry.controller?.abort()
      clearTimeout(entry.timeout)
    }
  }, [roomName, enabled])

  /**
   * Передаёт сетевой app-retry, который временно размонтирует LiveKitRoom.
   */
  const reportNetworkProblem = useCallback(() => {
    const entry = entryRef.current

    if (!entry?.active) {
      return
    }

    entry.manualRetry = true
    setState((previous) => (previous?.entry === entry ? { ...previous, problem: true } : previous))
  }, [])

  /** Запоминает показ, включая закрытие пользователем, до следующего захода. */
  const markWarningShown = useCallback(() => {
    const entry = entryRef.current

    if (!entry?.active) {
      return
    }

    entry.warned = true
    setState((previous) => (previous?.entry === entry ? { ...previous } : previous))
  }, [])

  useEffect(() => {
    const entry = entryRef.current

    if (!enabled || !entry || !entry.active) {
      return
    }

    /** Запускает неблокирующую проверку, не повторяя её на SDK/app-retry. */
    const check = () => {
      if (!accessToken || entry.requested) {
        return
      }

      entry.requested = true
      entry.controller = new AbortController()
      entry.timeout = setTimeout(() => entry.controller.abort(), 4500)

      void requestVpnAssessment(roomName, accessToken, entry.controller.signal).then(
        (assessment) => {
          clearTimeout(entry.timeout)

          if (entry.active) {
            setState((previous) =>
              previous?.entry === entry ? { ...previous, assessment } : previous,
            )
          }
        },
      )
    }

    /**
     * Обновляет только текущую локальную проблему, не сохраняя уже закончившуюся деградацию.
     * @param problem — есть ли сейчас плохое соединение или попытка восстановления.
     */
    const setProblem = (problem: boolean) => {
      setState((previous) => (previous?.entry === entry ? { ...previous, problem } : previous))
    }

    /**
     * Отслеживает начало connect, восстановление и SDK-reconnect.
     * @param connection — новое состояние текущего Room.
     */
    const handleState = (connection: ConnectionState) => {
      if (
        connection === ConnectionState.Connecting ||
        connection === ConnectionState.Connected ||
        connection === ConnectionState.Reconnecting ||
        connection === ConnectionState.SignalReconnecting
      ) {
        check()
      }

      if (
        connection === ConnectionState.Reconnecting ||
        connection === ConnectionState.SignalReconnecting
      ) {
        setProblem(true)
      } else if (connection === ConnectionState.Connected) {
        entry.manualRetry = false
        setProblem(
          room.localParticipant.connectionQuality === ConnectionQuality.Poor ||
            room.localParticipant.connectionQuality === ConnectionQuality.Lost,
        )
      }
    }

    /**
     * Исключает качество удалённого участника из VPN-подсказки.
     * @param quality — качество соединения участника.
     * @param participant — участник из события LiveKit.
     */
    const handleQuality = (quality: ConnectionQuality, participant: unknown) => {
      if (participant !== room.localParticipant || room.state === ConnectionState.Disconnected) {
        return
      }

      // Старый Room может прислать Good/Unknown во время ручного disconnect для retry.
      setProblem(
        entry.manualRetry ||
          room.state === ConnectionState.Reconnecting ||
          room.state === ConnectionState.SignalReconnecting ||
          quality === ConnectionQuality.Poor ||
          quality === ConnectionQuality.Lost,
      )
    }

    /**
     * Снимает отложенную подсказку при намеренном выходе, удалении или отказе авторизации.
     * @param reason — причина разрыва; сетевой app-retry уже отмечен владельцем звонка.
     */
    const handleDisconnected = (reason?: DisconnectReason) => {
      if (reason === DisconnectReason.CLIENT_INITIATED && entry.manualRetry) {
        return
      }

      if (
        reason !== undefined &&
        reason !== DisconnectReason.UNKNOWN_REASON &&
        reason !== DisconnectReason.STATE_MISMATCH &&
        reason !== DisconnectReason.JOIN_FAILURE &&
        reason !== DisconnectReason.SIGNAL_CLOSE &&
        reason !== DisconnectReason.CONNECTION_TIMEOUT &&
        reason !== DisconnectReason.MEDIA_FAILURE
      ) {
        setProblem(false)
      }
    }

    room.on(RoomEvent.ConnectionStateChanged, handleState)
    room.on(RoomEvent.ConnectionQualityChanged, handleQuality)
    room.on(RoomEvent.Disconnected, handleDisconnected)
    handleState(room.state)

    return () => {
      room.off(RoomEvent.ConnectionStateChanged, handleState)
      room.off(RoomEvent.ConnectionQualityChanged, handleQuality)
      room.off(RoomEvent.Disconnected, handleDisconnected)
    }
  }, [room, roomName, accessToken, enabled])

  const current =
    enabled &&
    state?.entry === entryRef.current &&
    state?.entry.active &&
    state?.entry.roomName === roomName
      ? state
      : null
  const assessment = current?.assessment ?? null
  const vpnSuspected = assessment?.status === 'assessed' && assessment.score >= 1

  return {
    assessment,
    vpnSuspected,
    shouldWarn: vpnSuspected && current.problem && !current.entry.warned,
    markWarningShown,
    reportNetworkProblem,
  }
}
